// src/lib/supabase.js
import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://YOUR_PROJECT.supabase.co'
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_ANON_KEY'

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY)

// ── Data fetching ────────────────────────────────────────────────────────────

export async function fetchPortfolio() {
  const { data: stocks, error: e1 } = await supabase
    .from('portfolio_stocks')
    .select('*')
    .order('code')
  if (e1) throw e1

  // Latest price per stock
  const { data: prices, error: e2 } = await supabase
    .from('price_snapshots')
    .select('code, price, snapped_at')
    .order('snapped_at', { ascending: false })
  if (e2) throw e2

  // Deduplicate prices — keep latest per code
  const latestPrice = {}
  prices.forEach(p => { if (!latestPrice[p.code]) latestPrice[p.code] = p.price })

  const { data: settings, error: e3 } = await supabase
    .from('portfolio_settings')
    .select('key, value')
  if (e3) throw e3

  const s = Object.fromEntries((settings || []).map(r => [r.key, r.value]))
  const cash = {
    rdpt: parseFloat(s['rdpt_cash'] || '0'),
    rdn:  parseFloat(s['rdn_cash']  || '0'),
  }

  return { stocks, latestPrice, cash }
}

export async function updateStockField(code, field, value) {
  const { error } = await supabase
    .from('portfolio_stocks')
    .update({ [field]: value, updated_at: new Date().toISOString() })
    .eq('code', code)
  if (error) throw error
}

export async function updatePrice(code, price) {
  const { error } = await supabase
    .from('price_snapshots')
    .insert({ code, price })
  if (error) throw error
}

const STOCKBIT_BASE = process.env.NODE_ENV === 'production'
  ? '/api/stockbit'
  : 'https://exodus.stockbit.com'

export async function fetchStockbitPrices(codes, token) {
  const results = await Promise.allSettled(
    codes.map(async code => {
      const res = await fetch(
        `${STOCKBIT_BASE}/company-price-feed/v2/orderbook/companies/${code}`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      const price = json?.data?.lastprice ?? json?.data?.close
      if (!price) throw new Error('no price in response')
      return { code, price }
    })
  )

  const failed = []
  for (const [i, r] of results.entries()) {
    if (r.status === 'fulfilled') {
      await updatePrice(r.value.code, r.value.price)
    } else {
      failed.push(codes[i])
    }
  }
  return { failed }
}

export async function addStock({ code, intrinsic_value, total_lot, cost_price, rk_cost, rk_alloc }) {
  const { error } = await supabase
    .from('portfolio_stocks')
    .insert({ code, intrinsic_value, total_lot, cost_price, rk_cost, rk_alloc })
  if (error) throw error
}

export async function deleteStock(code) {
  const { error: e1 } = await supabase.from('price_snapshots').delete().eq('code', code)
  if (e1) throw e1
  const { error: e2 } = await supabase.from('portfolio_stocks').delete().eq('code', code)
  if (e2) throw e2
}

export async function updateCash(label, amount) {
  const key = label === 'RDPT Cash' ? 'rdpt_cash' : 'rdn_cash'
  const { error } = await supabase
    .from('portfolio_settings')
    .upsert({ key, value: String(amount), updated_at: new Date().toISOString() })
  if (error) throw error
}

export async function saveCalcSession(session) {
  const { data, error } = await supabase
    .from('calc_sessions')
    .insert(session)
    .select()
  if (error) throw error
  return data[0]
}

// ── Portfolio computation engine ─────────────────────────────────────────────

export function computePortfolio(stocks, latestPrice, cash) {
  const rdpt = cash.rdpt ?? 0
  const rdn  = cash.rdn  ?? 0
  const cashInvestable = rdpt + rdn

  const enriched = stocks.map(s => {
    const price    = latestPrice[s.code] || s.cost_price
    const mktVal   = s.total_lot * price * 100
    const costVal  = s.total_lot * s.cost_price * 100
    const gainPct  = s.cost_price > 0 ? (price - s.cost_price) / s.cost_price : 0
    const mos      = s.intrinsic_value > 0 ? (s.intrinsic_value - price) / s.intrinsic_value : 0
    const upside   = s.intrinsic_value > 0 ? (s.intrinsic_value - price) / price : 0
    const diskon   = s.rk_cost > 0 ? Math.max(0, (s.rk_cost - price) / s.rk_cost) : 0
    const deviation= s.rk_cost > 0 ? (s.rk_cost - s.cost_price) / s.rk_cost : 0
    return { ...s, price, mktVal, costVal, gainPct, mos, upside, diskon, deviation }
  })

  const totalMktVal  = enriched.reduce((a, s) => a + s.mktVal, 0)
  const totalCostVal = enriched.reduce((a, s) => a + s.costVal, 0)
  const totalEquity  = totalMktVal + cashInvestable

  const withAlloc = enriched.map(s => ({
    ...s,
    myAlloc: totalEquity > 0 ? s.mktVal / totalEquity : 0,
    drift:   totalEquity > 0 ? s.mktVal / totalEquity - s.rk_alloc : 0,
  }))

  return { stocks: withAlloc, totalMktVal, totalCostVal, totalEquity, rdpt, rdn, cashInvestable }
}

export function computeCalculator(portfolioData, extraCash) {
  const { stocks, totalMktVal } = portfolioData
  const budget   = portfolioData.rdn + extraCash
  // RDPT masuk ke total (basis alokasi), tapi bukan bagian dari budget beli hari ini
  const newTotal = totalMktVal + portfolioData.rdpt + portfolioData.rdn + extraCash

  const withGap = stocks.map(s => {
    const targetVal = s.rk_alloc * newTotal
    const gap       = targetVal - s.mktVal
    const diskon    = s.rk_cost > 0 ? Math.max(0, (s.rk_cost - s.price) / s.rk_cost) : 0
    return { ...s, targetVal, gap, diskon }
  })

  const underweight = withGap.filter(s => s.gap > 0)
  const maxGap    = Math.max(...underweight.map(s => s.gap), 1)
  const maxMos    = Math.max(...underweight.map(s => s.mos), 1)
  const maxDiskon = Math.max(...underweight.map(s => s.diskon), 0.001)

  const scored = withGap.map(s => {
    if (s.gap <= 0) {
      const newAlloc = newTotal > 0 ? s.mktVal / newTotal : 0
      return { ...s, sGap: 0, sMos: 0, sDiskon: 0, score: 0, lotBeli: 0, nilaiBelis: 0, newAlloc }
    }
    const sGap    = (s.gap    / maxGap)    * 0.40
    const sMos    = (s.mos    / maxMos)    * 0.35
    const sDiskon = (s.diskon / maxDiskon) * 0.25
    const score   = sGap + sMos + sDiskon
    const lotBeli = s.price > 0 ? Math.floor(s.gap / (s.price * 100)) : 0
    const nilaiBelis = lotBeli * s.price * 100
    const newMktVal = s.mktVal + nilaiBelis
    const newAlloc  = newTotal > 0 ? newMktVal / newTotal : 0
    const gapClosed = s.gap > 0 ? nilaiBelis / s.gap : 0
    const status    = Math.abs(newAlloc - s.rk_alloc) < 0.005 ? 'Selaras'
                    : newAlloc < s.rk_alloc ? 'Masih Gap' : 'Overweight'
    return { ...s, sGap, sMos, sDiskon, score, lotBeli, nilaiBelis, newAlloc, gapClosed, status }
  })

  const buyList = scored
    .filter(s => s.lotBeli > 0)
    .sort((a, b) => b.score - a.score)

  const totalDeploy = buyList.reduce((a, s) => a + s.nilaiBelis, 0)
  const sisaCash    = budget - totalDeploy
  const utilPct     = budget > 0 ? totalDeploy / budget : 0

  return { allStocks: scored, buyList, budget, newTotal, totalDeploy, sisaCash, utilPct }
}
